import { Pointer, Service } from '@yleisradio/areena-types';
import { useUILanguage } from 'hooks/useUILanguage';
import NextLink from 'next/link';
import React from 'react';
import { pointerToClientURL, serviceFromPointer } from 'utils/pointer';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { serviceFromClientPath } from 'utils/url';
import { trackLink } from 'services/yleAnalyticsSdk';
import logger from 'services/logger';

type PointerType = Pointer['type'];

const AREENA_POINTER_TYPES: readonly PointerType[] = [
  'clip',
  'program',
  'series',
  'app',
  'package',
  'service',
  'view',
  undefined,
];

const IN_APP_POINTER_TYPES_BY_SERVICE: Record<Service, readonly PointerType[]> =
  {
    radio: AREENA_POINTER_TYPES,
    // Use hard (plain <a>) navigation on tv pages until all tv pages are served from items
    tv: [],
  };

function isInAppPointer(
  pointer: Pointer,
  service: Service | undefined
): boolean {
  const targetService = serviceFromPointer(pointer);

  if (!service) return true;

  if (targetService && targetService !== service) return false;

  if (targetService === null || targetService === service) {
    return IN_APP_POINTER_TYPES_BY_SERVICE[service].includes(pointer.type);
  }
  return true;
}

type LinkProps = {
  children: React.ReactNode;
  className?: string | undefined;
  download?: boolean | undefined;
  id?: string | undefined;
  onClick?: React.MouseEventHandler<HTMLElement> | undefined;
  classNameCurrentPage?: string | undefined;
  pointer: Pointer;
  service?: Service;
} & React.AriaAttributes;

export const Link: React.FunctionComponent<LinkProps> = ({
  children,
  download,
  onClick,
  pointer,
  service: serviceOverride,
  classNameCurrentPage,
  ...restProps
}) => {
  const language = useUILanguage();
  const { asPath } = useRouter();

  const { areenaService } = useAreenaService();

  const service =
    serviceOverride ?? areenaService ?? serviceFromClientPath(asPath);

  const href = pointerToClientURL(pointer, language, service);
  const pathname = usePathname();

  if (!href) {
    return onClick ? (
      <button onClick={onClick} {...restProps}>
        {children}
      </button>
    ) : (
      <span {...restProps}>{children}</span>
    );
  }

  const onClickWithTracking = (event: React.MouseEvent<HTMLAnchorElement>) => {
    try {
      trackLink(event.currentTarget);
    } catch (e) {
      logger.error(e, 'Tracking link failed');
    }

    if (onClick) {
      onClick(event);
    }
  };

  const className = classNames(
    restProps.className,
    pathname === href && classNameCurrentPage
  );

  if (isInAppPointer(pointer, service)) {
    return (
      <NextLink
        download={download}
        href={href}
        onClick={onClickWithTracking}
        {...restProps}
        className={className}
      >
        {children}
      </NextLink>
    );
  }

  if (AREENA_POINTER_TYPES.includes(pointer.type)) {
    return (
      <a
        download={download}
        href={href}
        onClick={onClickWithTracking}
        {...restProps}
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      download={download}
      href={href}
      onClick={onClickWithTracking}
      rel="external"
      target="_blank"
      {...restProps}
      className={className}
    >
      {children}
    </a>
  );
};
